import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight, Brain, Database, Shield, Leaf, GraduationCap } from "lucide-react";
import { useI18n } from "@/lib/i18n";
import { BD } from "@/constants/testIds";
import { EISG_IMG } from "@/lib/data";
import SectionHeader from "@/components/shared/SectionHeader";

const PILLARS = [
  { key: "ai", icon: Brain, span: "md:col-span-7" },
  { key: "data", icon: Database, span: "md:col-span-5" },
  { key: "gov", icon: Shield, span: "md:col-span-5" },
  { key: "sus", icon: Leaf, span: "md:col-span-4" },
  { key: "exec", icon: GraduationCap, span: "md:col-span-3" },
];

const BDHome = () => {
  const { t } = useI18n();
  return (
    <div>
      {/* HERO */}
      <section className="relative min-h-[92vh] flex items-center overflow-hidden">
        <div className="absolute inset-0">
          <img src={EISG_IMG.hero} alt="BridgeDatum" className="w-full h-full object-cover" />
          <div className="absolute inset-0 hero-overlay-navy" />
        </div>
        <div className="relative max-w-[1400px] mx-auto px-6 lg:px-10 py-32 w-full">
          <div className="max-w-3xl text-white">
            <div className="flex items-center gap-3 mb-8">
              <span className="accent-bar" />
              <span className="overline text-[#D4AF37]">{t("home.hero.eyebrow")}</span>
            </div>
            <h1 className="font-bd text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.02]">
              {t("home.hero.headline")}
            </h1>
            <p className="mt-10 font-bd font-semibold text-lg sm:text-xl lg:text-2xl leading-snug text-white tracking-tight max-w-3xl">
              {t("home.hero.subtitle")}
            </p>
            <p className="mt-6 text-base sm:text-lg text-white/75 leading-relaxed max-w-3xl font-body">
              {t("home.hero.body")}
            </p>
            <div className="mt-10 flex flex-wrap gap-4">
              <Link to="/solutions" data-testid={BD.heroCtaSolutions} className="btn-premium btn-premium-gold">
                {t("home.hero.cta.solutions")} <ArrowRight size={16} />
              </Link>
              <Link to="/eisg" data-testid={BD.heroCtaEisg} className="btn-premium btn-premium-ghost">
                {t("home.hero.cta.eisg")} <ArrowUpRight size={16} />
              </Link>
            </div>
            <div className="mt-16 grid grid-cols-3 gap-6 max-w-lg border-t border-white/15 pt-8">
              <div>
                <div className="font-eisg text-3xl text-[#D4AF37]">15+</div>
                <div className="text-[11px] uppercase tracking-[0.2em] text-white/60 mt-1">{t("home.stats.countries")}</div>
              </div>
              <div>
                <div className="font-eisg text-3xl text-[#D4AF37]">100%</div>
                <div className="text-[11px] uppercase tracking-[0.2em] text-white/60 mt-1">{t("home.stats.executive")}</div>
              </div>
              <div>
                <div className="font-eisg text-3xl text-[#D4AF37]">5</div>
                <div className="text-[11px] uppercase tracking-[0.2em] text-white/60 mt-1">{t("home.stats.disciplines")}</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* PILLARS */}
      <section className="py-24 lg:py-32 bg-[#FAFAFA]">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <SectionHeader
            number={t("home.pillars.number")}
            eyebrow={t("home.pillars.eyebrow")}
            title={t("home.pillars.title")}
            subtitle={t("home.pillars.subtitle")}
            body={t("home.pillars.body")}
          />
          <div className="mt-16 grid grid-cols-1 md:grid-cols-12 gap-6">
            {PILLARS.map(({ key, icon: Icon, span }) => (
              <div
                key={key}
                className={`${span} lift-card bg-white border border-[#0A192F]/10 p-10 group`}
              >
                <Icon size={28} className="text-[#0A192F] group-hover:text-[#F58220] transition-colors" />
                <h3 className="mt-6 font-bd text-2xl text-[#0A192F] tracking-tight">
                  {t(`home.pillars.${key}.title`)}
                </h3>
                <p className="mt-3 text-[#0A192F]/70 leading-relaxed text-[15px]">
                  {t(`home.pillars.${key}.desc`)}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* APPROACH / QUOTE */}
      <section className="py-24 lg:py-32 bg-[#0A192F] text-white grain relative">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 relative">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-12 items-center">
            <div className="md:col-span-7">
              <div className="overline text-[#D4AF37] mb-6">{t("home.approach.number")}</div>
              <p className="font-eisg text-3xl sm:text-4xl lg:text-5xl leading-[1.15] tracking-tight">
                "{t("home.approach.quote.a")} <span className="text-[#D4AF37] italic">{t("home.approach.quote.b")}</span>"
              </p>
              <p className="mt-8 text-white/70 max-w-xl leading-relaxed">
                {t("home.approach.body")}
              </p>
            </div>
            <div className="md:col-span-5">
              <img src={EISG_IMG.capeTown} alt="Cape Town" className="w-full h-[440px] object-cover" />
            </div>
          </div>
        </div>
      </section>

      {/* EISG BRIDGE */}
      <section className="py-24 lg:py-32 bg-white">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-6 order-2 lg:order-1">
              <img src={EISG_IMG.boardroomExec} alt="EISG executive boardroom" className="w-full h-[520px] object-cover" />
            </div>
            <div className="lg:col-span-6 order-1 lg:order-2">
              <div className="section-num mb-3">{t("home.eisgBridge.number")}</div>
              <div className="overline text-[#F58220] mb-4">{t("home.eisgBridge.eyebrow")}</div>
              <h2 className="font-eisg text-3xl sm:text-4xl lg:text-5xl text-[#0A192F] tracking-tight leading-[1.02] font-bold">
                {t("home.eisgBridge.title")}
              </h2>
              <p className="mt-6 font-bd font-semibold text-lg sm:text-xl lg:text-2xl text-[#0A192F]/90 leading-snug tracking-tight max-w-xl">
                {t("home.eisgBridge.subtitle")}
              </p>
              <p className="mt-5 text-[#0A192F]/70 leading-relaxed text-base max-w-xl">
                {t("home.eisgBridge.body")}
              </p>
              <div className="mt-6 grid grid-cols-3 gap-4 max-w-md">
                {["pilotage", "command", "functions"].map((k) => (
                  <div key={k} className="border-l-2 border-[#D4AF37] pl-3">
                    <div className="text-[10px] uppercase tracking-[0.2em] text-[#0A192F]/60">{t("home.eisgBridge.moment")}</div>
                    <div className="font-bd font-semibold text-[#0A192F]">{t(`home.eisgBridge.${k}`)}</div>
                  </div>
                ))}
              </div>
              <Link to="/eisg" className="mt-10 inline-flex items-center gap-2 btn-premium btn-premium-primary">
                {t("home.eisgBridge.cta")} <ArrowUpRight size={16} />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* CTA banner */}
      <section className="py-20 bg-[#FAFAFA] border-t border-[#0A192F]/10">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 flex flex-col lg:flex-row items-start lg:items-center justify-between gap-8">
          <div>
            <h3 className="font-bd text-3xl sm:text-4xl text-[#0A192F] tracking-tight max-w-2xl font-bold">
              {t("home.cta.title")}
            </h3>
          </div>
          <Link to="/contact" className="btn-premium btn-premium-primary">
            {t("home.cta.btn")} <ArrowRight size={16} />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default BDHome;
